import { AxiosInstance } from "axios";

import moment from "moment";

import { WalletUrlGenerator } from "./url-generator";
import { WalletFilterType } from "./enums";

import {
  AdminCurrencyShowResponse,
  DepositAddressResposne,
} from "../admin/currency/repo";
import {
  AdminCurrencyShowResponseFactory,
  AdminChainShowResponseFactory,
} from "../admin/currency/factory";

import {
  WalletListResponse,
  WalletWithAmountResponse,
  WalletHistoryResponse,
  AdminWalletHistoryResponse,
  WalletTotalBalanceResponse,
  WalletTradingPairResponse,
  RatesTickerRequest,
  RatesTickerResponse,
} from "./repo";
import {
  WalletListResponseFactory,
  WalletWithAmountResponseFactory,
  DepositAddressResposneFactory,
  WalletTotalBalanceResponseFactory,
  WalletTradingPairResponseFactory,
} from "./factory";

import {
  ResponseData,
  ResponseDataData,
  HistoryFilterRequest,
  AdminHistoryFilterRequest,
} from "@/shared/constants/interfaces";
import { AdminCurrencyType } from "../admin/currency/enums";
import { AdminChainListResponse } from "@/shared/repository/modules/admin/currency/repo";

export interface WalletProjectRepository {
  list(type: WalletFilterType, userId: number): Promise<WalletListResponse[]>;
  currency(): Promise<AdminCurrencyShowResponse[]>;
  withAmount(
    type: WalletFilterType,
    userId: number
  ): Promise<WalletWithAmountResponse[]>;
  create(): Promise<void>;
  chains(coinId: number): Promise<AdminChainListResponse[]>;
  deposit(symbol: string, chain: string): Promise<DepositAddressResposne>;
  withdrawal(payload: {
    coin: string;
    chain: string;
    tag: string;
    address: string;
    amount: number;
  }): Promise<void>;
  verification(payload: { code: string }): Promise<void>;
  history(payload: HistoryFilterRequest): Promise<WalletHistoryResponse[]>;
  adminHistory(
    payload: AdminHistoryFilterRequest
  ): Promise<AdminWalletHistoryResponse[]>;
  totalBalance(payload: {
    fiat: string;
    crypto: string;
  }): Promise<WalletTotalBalanceResponse>;
  tradingPairs(payload: {
    from: number;
    to: number;
  }): Promise<WalletTradingPairResponse>;
  exchange(payload: {
    from: number;
    to: number;
    side: string;
    symbol: string;
    amount: number;
    created: string;
    userId: number;
  }): Promise<void>;
  verificationExchange(payload: { code: string }): Promise<void>;
  ticker(payload: RatesTickerRequest): Promise<RatesTickerResponse>;
}

export class WalletHttpRepo implements WalletProjectRepository {
  constructor(
    private readonly axios: AxiosInstance,
    private readonly urlGenerator: WalletUrlGenerator
  ) {}

  async list(
    type: WalletFilterType,
    userId: number
  ): Promise<WalletListResponse[]> {
    const { data } = await this.axios.get<ResponseData<WalletListResponse[]>>(
      this.urlGenerator.list(userId, type)
    );

    return data.data
      .filter((item) => {
        if (type === WalletFilterType.all) return true;
        else if (type === WalletFilterType.fiat)
          return item.type === AdminCurrencyType.fiat;

        return item.type === AdminCurrencyType.crypto;
      })
      .map((item) => WalletListResponseFactory(item))
      .sort((a, b) => {
        if (
          a.type === AdminCurrencyType.fiat &&
          b.type !== AdminCurrencyType.fiat
        )
          return -1;
        if (
          a.type !== AdminCurrencyType.fiat &&
          b.type === AdminCurrencyType.fiat
        )
          return 1;

        return b.amount - a.amount;
      });
  }

  async currency(): Promise<AdminCurrencyShowResponse[]> {
    const { data } = await this.axios.get<
      ResponseData<AdminCurrencyShowResponse[]>
    >(this.urlGenerator.currency());

    return data.data.map((item) => AdminCurrencyShowResponseFactory(item));
  }

  async chains(coinId: number): Promise<AdminChainListResponse[]> {
    const { data } = await this.axios.get<
      ResponseData<AdminChainListResponse[]>
    >(this.urlGenerator.chains(coinId));

    return data.data.map((item) => AdminChainShowResponseFactory(item));
  }

  async deposit(
    symbol: string,
    chain: string
  ): Promise<DepositAddressResposne> {
    const { data } = await this.axios.get<ResponseData<DepositAddressResposne>>(
      this.urlGenerator.depositAddress(symbol, chain)
    );
    return DepositAddressResposneFactory(data.data ?? {});
  }

  async withAmount(
    type: WalletFilterType,
    userId: number
  ): Promise<WalletWithAmountResponse[]> {
    const { data } = await this.axios.get<
      ResponseData<WalletWithAmountResponse[]>
    >(this.urlGenerator.withAmount(userId, type));

    return data.data.map((item) => WalletWithAmountResponseFactory(item));
  }

  async create(): Promise<void> {
    try {
      await this.axios.post<void>(this.urlGenerator.create());
    } catch (error) {
      console.error("Error in create user wallet:", error);
    }
  }

  async withdrawal(payload: {
    coin: string;
    chain: string;
    tag: string;
    address: string;
    amount: number;
  }): Promise<void> {
    await this.axios.post<void>(this.urlGenerator.withdrawal(), payload);
  }

  async verification(payload: { code: string }): Promise<void> {
    await this.axios.post<void>(this.urlGenerator.verification(), payload);
  }

  async history(
    payload: HistoryFilterRequest
  ): Promise<WalletHistoryResponse[]> {
    const { data } = await this.axios.get<
      | ResponseDataData<WalletHistoryResponse[]>
      | ResponseData<WalletHistoryResponse[]>
    >(this.urlGenerator.history(), {
      params: {
        "filter[date_from]": payload.date_from ? payload.date_from : undefined,
        "filter[date_to]": payload.date_to ? payload.date_to : undefined,
        "filter[amount_from]": payload.amount_from
          ? payload.amount_from
          : undefined,
        "filter[amount_to]": payload.amount_to ? payload.amount_to : undefined,
        "filter[status]": payload.status ? payload.status : undefined,
        "filter[currency_id]": payload.currency_id
          ? payload.currency_id
          : undefined,
        "filter[app_type]": payload.type ? payload.type : undefined,
        "filter[coin]": payload.coin ? payload.coin : undefined,
        page: payload.page,
        per_page: payload.per_page,
      },
    });

    return "data" in data.data
      ? data.data.data.map((item) => {
          item["created_at"] = moment(item["created_at"] ?? "").format(
            "DD.MM.YYYY HH:mm:ss"
          );
          return item;
        })
      : data.data.map((item) => {
          item["created_at"] = moment(item["created_at"] ?? "").format(
            "DD.MM.YYYY HH:mm:ss"
          );
          return item;
        });
  }

  async adminHistory(
    payload: AdminHistoryFilterRequest
  ): Promise<AdminWalletHistoryResponse[]> {
    const { data } = await this.axios.get<
      | ResponseDataData<AdminWalletHistoryResponse[]>
      | ResponseData<AdminWalletHistoryResponse[]>
    >(this.urlGenerator.adminHistory(), {
      params: {
        "filter[date_from]": payload.date_from ? payload.date_from : undefined,
        "filter[date_to]": payload.date_to ? payload.date_to : undefined,
        "filter[type]": payload.type ? payload.type : undefined,
        "filter[status]": payload.status ? payload.status : undefined,
        "filter[coin]": payload.coin ? payload.coin : undefined,
        page: payload.page,
        per_page: payload.per_page,
      },
    });

    return "data" in data.data
      ? data.data.data.map((item) => {
          item["created_at"] = moment(item["created_at"] ?? "").format(
            "DD.MM.YYYY HH:mm:ss"
          );
          return item;
        })
      : data.data.map((item) => {
          item["created_at"] = moment(item["created_at"] ?? "").format(
            "DD.MM.YYYY HH:mm:ss"
          );
          return item;
        });
  }

  async totalBalance(payload: {
    fiat: string;
    crypto: string;
  }): Promise<WalletTotalBalanceResponse> {
    const response = await this.axios.post<
      ResponseData<WalletTotalBalanceResponse>
    >(this.urlGenerator.totalBalance(), {
      fiat: payload.fiat,
      crypto: payload.crypto,
    });
    return WalletTotalBalanceResponseFactory(response.data.data);
  }

  async exchange(payload: {
    from: number;
    to: number;
    side: string;
    symbol: string;
    amount: number;
    created: string;
    userId: number;
  }): Promise<void> {
    await this.axios.post<void>(this.urlGenerator.exchange(), payload);
  }

  async verificationExchange(payload: { code: string }): Promise<void> {
    await this.axios.post<void>(
      this.urlGenerator.verificationExchange(),
      payload
    );
  }

  async tradingPairs(payload: {
    from: number;
    to: number;
  }): Promise<WalletTradingPairResponse> {
    const { data } = await this.axios.get<
      ResponseData<WalletTradingPairResponse>
    >(this.urlGenerator.tradingPairs(payload.from, payload.to));
    return WalletTradingPairResponseFactory(data.data ?? {});
  }

  async ticker(payload: RatesTickerRequest): Promise<RatesTickerResponse> {
    const { data } = await this.axios.post<ResponseData<RatesTickerResponse>>(
      this.urlGenerator.ticker(),
      payload
    );
    return data.data ?? { quotes: [] };
  }
}
